import React, { useEffect, useState } from "react";
import axios from "axios";
import MuiAlert from "@material-ui/lab/Alert";
import { CardElement, Elements, ElementsConsumer } from "@stripe/react-stripe-js";
import { loadStripe, Stripe, StripeElements } from "@stripe/stripe-js";
import { API_ENDPOINT, STRIPE_PUBLIC_TOKEN, supportEmail } from "../../../constants";
import { ValidateEmail } from "../../../utils/utils";
import { isRegistrationClosed } from "../../../utils/dateUtils";
import { appendApiMessage } from "../../../utils/apiError";
import Loader from "../../../components/loader";
import TopSnackbar from "../../../components/TopSnackbar";
import { v4 as uuidv4 } from "uuid";
import { resolvePublicSsClass, studentExists } from "../../../services/SsClassPublicService";
import { SsStudentExistsResponse } from "../../../types/self-serve.public.types";
import "../../../assets/css/stripe.css";

/**
 * Register and pay tab on the public class landing page.
 *
 * Continue calls `POST /api/public/ss-classes/{slug}/exists` with `{ email, slug }`.
 * The response is `{ studentExists, paymentDone }`. Both true shows the already-paid
 * success message. `studentExists` alone shows the card. Both false also shows name.
 * Register reloads the class before Stripe. Payment is refused when the expiry
 * is today or already past, the class is inactive (410) or deleted
 * (404), or Register and pay is disabled.
 * Pay Now and Register and pay both call
 * `POST /api/public/ss-classes/{slug}/students` with plain axios.
 * An empty card shows a required error and does not call the API.
 * For a new student, an empty name and an empty card both show on the same click.
 * `existingStudent` is true for Pay Now and false for Register and pay.
 * A 402 decline keeps the email, clears the name when that email is new, and clears the card.
 * The credit-card message closes after one minute.
 */

const REGISTRATION_CLOSED = "This class is no longer accepting registrations.";

const paymentDeclinedMessage = (email: string) =>
  `Thank you for attempting to pay for your SchoolSims subscription. Unfortunately there was an issue with your payment. Please check with your credit card company and if you need further details, please email ${email}.`;

/** True when this landing snapshot must not be charged. */
const classRefusesPay = (clazz: {
  expiryDate?: string | null;
  landingOptionPay?: boolean;
} | null | undefined) =>
  !clazz?.landingOptionPay || isRegistrationClosed(clazz?.expiryDate);

const responseMessage = (data: any) => {
  if (typeof data === "string" && data.trim()) {
    return data.trim();
  }
  if (typeof data?.message === "string" && data.message.trim()) {
    return data.message.trim();
  }
  return "";
};

const stripePromise = loadStripe(STRIPE_PUBLIC_TOKEN);

const CARD_ELEMENT_OPTIONS = {
  hidePostalCode: false,
  style: {
    base: {
      fontFamily: 'Inter, "Segoe UI", system-ui, sans-serif',
      fontSize: "14px",
      fontWeight: "400",
      color: "#2a2a2c",
      "::placeholder": {
        color: "#aaa",
        fontWeight: "300",
      },
    },
    invalid: {
      color: "#dc3545",
    },
  },
};

type SelfServeCheckoutFormProps = {
  stripe: Stripe | null;
  elements: StripeElements | null;
  existingStudent: boolean;
  slug: string;
  email: string;
  name: string;
  onInvalidName: () => void;
  onPaymentResult: (severity: "success" | "error" | "declined", message: string) => void;
  registrationClosed: boolean;
};

/** Card field plus Pay Now or Register and pay. */
const SelfServeCheckoutForm: React.FC<SelfServeCheckoutFormProps> = ({
  stripe,
  elements,
  existingStudent,
  slug,
  email,
  name,
  onInvalidName,
  onPaymentResult,
  registrationClosed,
}) => {
  const [cardError, setCardError] = useState("");
  const [cardEmpty, setCardEmpty] = useState(true);
  const [paying, setPaying] = useState(false);

  const focusRegister = () => {
    requestAnimationFrame(() => {
      document.getElementById("class-pay-register")?.focus();
    });
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (paying) {
      return;
    }
    if (!stripe || !elements) {
      setCardError("Payment form is still loading. Please try again.");
      focusRegister();
      return;
    }
    if (registrationClosed) {
      onPaymentResult("error", REGISTRATION_CLOSED);
      return;
    }
    const trimmedName = name.trim();
    const nameMissing = !existingStudent && !trimmedName;
    if (nameMissing) {
      onInvalidName();
    }
    if (cardEmpty) {
      setCardError("The card field is required.");
    }
    if (nameMissing || cardEmpty) {
      focusRegister();
      return;
    }
    const cardElement = elements.getElement(CardElement);
    if (!cardElement) {
      return;
    }
    setPaying(true);
    let freshClass: any;
    try {
      freshClass = await resolvePublicSsClass(slug);
    } catch (error: any) {
      setPaying(false);
      const status = error?.response?.status;
      if (status === 404 || status === 410) {
        onPaymentResult("error", REGISTRATION_CLOSED);
        return;
      }
      onPaymentResult("error", "Unable to verify this class. Please try again.");
      return;
    }
    if (classRefusesPay(freshClass)) {
      setPaying(false);
      onPaymentResult("error", REGISTRATION_CLOSED);
      return;
    }
    try {
      const { error, paymentMethod } = await stripe.createPaymentMethod({
        type: "card",
        card: cardElement,
      });
      if (error || !paymentMethod) {
        setCardError(error?.message || "Unable to submit this card.");
        setPaying(false);
        focusRegister();
        return;
      }
      const { source } = await stripe.createSource(cardElement, {
        type: "card",
        currency: "usd",
      });
      const paymentPayload = {
        uniqueToken: uuidv4(),
        stripeToken: paymentMethod.id,
        sourceId: source?.id,
      };
      const payment = await axios.post(
        `${API_ENDPOINT}public/ss-classes/${encodeURIComponent(slug)}/students`,
        {
          stdName: trimmedName,
          classId: freshClass?.classId ?? freshClass?.id,
          amount: freshClass?.paymentAmount,
          email: email.trim(),
          existingStudent,
          paymentTokens: paymentPayload,
        },
        { withCredentials: false }
      );
      setPaying(false);
      const message = responseMessage(payment?.data);
      const status = Number(payment?.data?.status ?? payment?.status);
      if (status === 200 || status === 201 || status === 2001) {
        onPaymentResult("success", message || "Success");
        return;
      }
      if (status === 402) {
        onPaymentResult("declined", message || "Payment failed.");
        return;
      }
      onPaymentResult("error", message || "Payment failed.");
    } catch (error: any) {
      setPaying(false);
      if (error?.response?.status === 402) {
        onPaymentResult(
          "declined",
          responseMessage(error?.response?.data) || "Payment failed."
        );
        return;
      }
      await appendApiMessage(error, "Payment failed.");
      onPaymentResult(
        "error",
        error?.userMessage || responseMessage(error?.response?.data) || "Payment failed."
      );
    }
  };

  return (
    <form className="stripPaymentForm" onSubmit={handleSubmit}>
      <div
        className="form-group"
        aria-invalid={Boolean(cardError)}
        aria-describedby={cardError ? "class-pay-card-error" : undefined}
      >
        <label htmlFor="class-pay-card" className="required">Enter card details</label>
        <CardElement
          id="class-pay-card"
          onChange={(event) => {
            setCardEmpty(event.empty);
            setCardError("");
            if (event.complete) {
              focusRegister();
            }
          }}
          options={CARD_ELEMENT_OPTIONS}
        />
        {cardError ? (
          <div id="class-pay-card-error" className="invalid-feedback d-block" role="alert">
            {cardError}
          </div>
        ) : null}
      </div>
      <div className="ss-public-actions">
        <button
          type="button"
          id="class-pay-register"
          className="edsimsV2-pill-button edsimsV2-pill-button--primary"
          aria-label="Register"
          aria-busy={paying}
          aria-disabled={paying}
          onClick={(event) => {
            event.preventDefault();
            void handleSubmit(event);
          }}
        >
          Register
        </button>
      </div>
      {paying ? (
        <div className="overlay">
          <Loader loading={paying} />
        </div>
      ) : null}
    </form>
  );
};

const SelfServeCardDetails: React.FC<{
  existingStudent: boolean;
  slug: string;
  email: string;
  name: string;
  onInvalidName: () => void;
  onPaymentResult: (severity: "success" | "error" | "declined", message: string) => void;
  registrationClosed: boolean;
}> = ({
  existingStudent,
  slug,
  email,
  name,
  onInvalidName,
  onPaymentResult,
  registrationClosed,
}) => (
  <Elements stripe={stripePromise}>
    <ElementsConsumer>
      {({ elements, stripe }) => (
        <SelfServeCheckoutForm
          elements={elements}
          stripe={stripe}
          existingStudent={existingStudent}
          slug={slug}
          email={email}
          name={name}
          onInvalidName={onInvalidName}
          onPaymentResult={onPaymentResult}
          registrationClosed={registrationClosed}
        />
      )}
    </ElementsConsumer>
  </Elements>
);

type RegisterAndPayProps = {
  clazz: any;
  slug: string;
};

const RegisterAndPay: React.FC<RegisterAndPayProps> = ({ clazz, slug }) => {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [error, setError] = useState("");
  const [existsResult, setExistsResult] = useState<SsStudentExistsResponse | null>(null);
  const [paid, setPaid] = useState(false);
  const [declined, setDeclined] = useState(false);
  const [cardKey, setCardKey] = useState(0);
  const [notice, setNotice] = useState({
    open: false,
    message: "",
    severity: "success" as "success" | "error",
  });

  useEffect(() => {
    if (!declined) {
      return;
    }
    const timer = window.setTimeout(() => setDeclined(false), 60000);
    return () => window.clearTimeout(timer);
  }, [declined, cardKey]);

  const onContinue = async () => {
    setDeclined(false);
    const trimmed = email.trim();
    if (!trimmed) {
      setExistsResult(null);
      setError("The email field is required.");
      return;
    }
    if (!ValidateEmail(trimmed)) {
      setExistsResult(null);
      setError("The email must be a valid email address.");
      return;
    }
    if (!slug) {
      setError("Unable to verify this email. Please try again.");
      return;
    }
    setError("");
    setExistsResult(null);
    setEmail(trimmed);
    try {
      const exists = await studentExists(slug, trimmed);
      setExistsResult(exists);
    } catch {
      setError("Unable to verify this email. Please try again.");
    }
  };

  const registrationClosed = isRegistrationClosed(clazz?.expiryDate);
  const alreadyPaid =
    existsResult?.studentExists === true && existsResult?.paymentDone === true;
  const existingStudent =
    existsResult == null
      ? null
      : existsResult.studentExists === true && existsResult.paymentDone !== true;

  const onInvalidName = () => {
    setError("The name field is required.");
  };

  const onPaymentResult = (severity: "success" | "error" | "declined", message: string) => {
    if (severity === "success") {
      setPaid(true);
      setNotice({ open: true, severity, message });
      return;
    }
    if (severity === "declined") {
      setError("");
      if (existingStudent === false) {
        setName("");
      }
      setCardKey((key) => key + 1);
      setDeclined(true);
      setNotice({ open: true, severity: "error", message });
      return;
    }
    setNotice({ open: true, severity, message });
  };

  const closeNotice = () => {
    setNotice((current) => ({ ...current, open: false }));
  };

  return (
    <>
      <TopSnackbar
        open={notice.open}
        message={notice.message}
        severity={notice.severity}
        onClose={closeNotice}
      />
      {paid || alreadyPaid ? (
        <div className="ss-pay-success" role="status">
          <MuiAlert severity="success" variant="standard" elevation={6}>
            {alreadyPaid && !paid ? (
              <>
                Your payment is already done.
                <br />
                Please check your inbox for more details and in case of queries contact{" "}
                <a href={`mailto:${supportEmail}`}>{supportEmail}</a>.
              </>
            ) : (
              <>
                Your payment has been processed successfully. Thank you!
                <br />
                You will receive an email from SchoolSims with valid Access code for
                accessing simulations. If you do not see an email in a few minutes,
                please check your spam folder.
              </>
            )}
          </MuiAlert>
        </div>
      ) : (
      <>
      {declined ? (
        <div className="ss-pay-success" role="alert">
          <MuiAlert severity="error" variant="standard" elevation={6}>
            {paymentDeclinedMessage(supportEmail)}
          </MuiAlert>
        </div>
      ) : null}
      <p className="text-muted" style={{ fontSize: 14 }}>
        Continue & Pay to complete your purchase.
        {clazz.paymentAmount != null ? (
          <>
            {" "}
            Amount: <strong>${Number(clazz.paymentAmount).toFixed(2)}</strong>
          </>
        ) : null}
      </p>
      <form
        onSubmit={(event) => {
          event.preventDefault();
          void onContinue();
        }}
        noValidate
      >
        <div className="form-group">
          <label htmlFor="class-pay-email" className="required">Email</label>
          <input
            id="class-pay-email"
            name="email"
            type="email"
            maxLength={50}
            autoComplete="email"
            placeholder="Email Address"
            className={`form-control ${
              error && existingStudent === null ? "is-invalid" : ""
            }`}
            value={email}
            readOnly={existingStudent !== null}
            aria-readonly={existingStudent !== null}
            aria-required="true"
            aria-invalid={Boolean(error && existingStudent === null)}
            aria-describedby={
              error && existingStudent === null ? "class-pay-email-error" : undefined
            }
            onChange={(event) => {
              setExistsResult(null);
              setError("");
              setEmail(event.target.value);
            }}
            onKeyDown={(event) => {
              if (event.key !== "Enter" || existsResult !== null) {
                return;
              }
              event.preventDefault();
              void onContinue();
            }}
          />
          {error && existingStudent === null ? (
            <div id="class-pay-email-error" className="invalid-feedback">
              {error}
            </div>
          ) : null}
        </div>
        <div className="ss-public-actions">
          <button
            type="submit"
            className="edsimsV2-pill-button edsimsV2-pill-button--primary"
            disabled={existsResult !== null}
          >
            Continue & Pay
          </button>
        </div>
      </form>
      {existingStudent === true ? (
        <div className="mt-3">
          <SelfServeCardDetails
            key={cardKey}
            existingStudent
            slug={slug}
            email={email}
            name={name}
            onInvalidName={onInvalidName}
            onPaymentResult={onPaymentResult}
            registrationClosed={registrationClosed}
          />
        </div>
      ) : null}
      {existingStudent === false ? (
        <>
          <div className="form-group mt-3">
            <label htmlFor="class-pay-name" className="required">Name</label>
            <input
              id="class-pay-name"
              name="name"
              type="text"
              maxLength={500}
              autoComplete="name"
              placeholder="Name"
              className={`form-control ${error ? "is-invalid" : ""}`}
              value={name}
              aria-required="true"
              aria-invalid={Boolean(error)}
              aria-describedby={error ? "class-pay-name-error" : undefined}
              onChange={(event) => {
                setError("");
                setName(event.target.value);
              }}
            />
            {error ? (
              <div id="class-pay-name-error" className="invalid-feedback" role="alert">
                {error}
              </div>
            ) : null}
          </div>
          <SelfServeCardDetails
            key={cardKey}
            existingStudent={false}
            slug={slug}
            email={email}
            name={name}
            onInvalidName={onInvalidName}
            onPaymentResult={onPaymentResult}
            registrationClosed={registrationClosed}
          />
        </>
      ) : null}
      </>
      )}
    </>
  );
};

export default RegisterAndPay;
